import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Loader2,
  Type,
  MessageSquare,
  Check,
  Undo2,
  Redo2,
  ClipboardList,
  MousePointer2,
  Pencil,
  Plus,
  ImagePlus,
  Camera,
} from 'lucide-react';
import imageGenerationService, {
  TextElement,
  GeneratedImage,
} from '../../services/api/imageGenerationService';
import { TaskySelect, useConfirm } from '../tasky';

// Edit mode
type EditMode = 'text' | 'region' | 'addText' | 'naturalPhoto';

// Text edit (existing text replacement)
interface TextEdit {
  elementId: number;
  originalText: string;
  newText: string;
}

// Region edit (drag-select area + AI instruction)
interface RegionEdit {
  id: number;
  region: { x: number; y: number; width: number; height: number };
  instruction: string;
  mode?: 'custom' | 'naturalPhoto';
}

interface PixelRegion {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

// Web font options
const FONT_OPTIONS = [
  { label: 'ゴシック', value: 'Noto Sans JP' },
  { label: '明朝', value: 'Noto Serif JP' },
  { label: '丸ゴシック', value: 'M PLUS Rounded 1c' },
  { label: '太ゴシック', value: 'BIZ UDPGothic' },
  { label: '手書き風', value: 'Yomogi' },
] as const;

const NATURAL_PHOTO_INSTRUCTION = [
  'iPhoneで日常的に撮影したような自然な写真に整えてください。',
  '過度な広告感、CG感、スタジオ撮影感、過剰なレタッチを抑えてください。',
  '元素材の構図、商品同一性、主要なロゴや読めるべき情報は維持してください。',
  '自然光、スマートフォン写真らしい質感、生活感のある色味に寄せてください。',
  '商品や人物の形状を別物に変えないでください。',
].join(' ');

// Add text (place new text on image)
interface AddTextEdit {
  id: number;
  position: { x: number; y: number };
  text: string;
  fontSize: number;
  color: string;
  fontFamily: string;
  textShadow: boolean;
}

interface ImageOverlayEdit {
  id: number;
  region: { x: number; y: number; width: number; height: number };
  imageData: string;
  previewUrl: string;
  fileName: string;
}

// Undo/Redo snapshot
interface EditSnapshot {
  textEdits: TextEdit[];
  regionEdits: RegionEdit[];
  addTextEdits: AddTextEdit[];
  imageOverlayEdits: ImageOverlayEdit[];
}

export interface SmartImageEditModalProps {
  image: GeneratedImage;
  isOpen: boolean;
  onClose: () => void;
  onApply: (editedImage: GeneratedImage) => void | Promise<void>;
}

const SmartImageEditModal: React.FC<SmartImageEditModalProps> = ({
  image,
  isOpen,
  onClose,
  onApply,
}) => {
  const confirm = useConfirm();

  // Edit mode
  const [editMode, setEditMode] = useState<EditMode>('region');

  // Text detection
  const [textElements, setTextElements] = useState<TextElement[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectError, setDetectError] = useState<string | null>(null);
  const [hasAttemptedTextDetection, setHasAttemptedTextDetection] = useState(false);

  // Text edits
  const [textEdits, setTextEdits] = useState<TextEdit[]>([]);
  const [selectedElementId, setSelectedElementId] = useState<number | null>(null);
  const [popoverTab, setPopoverTab] = useState<'text' | 'ai'>('text');
  const [popoverText, setPopoverText] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [hoveredElementId, setHoveredElementId] = useState<number | null>(null);

  // Region edits
  const [regionEdits, setRegionEdits] = useState<RegionEdit[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragCurrent, setDragCurrent] = useState<{ x: number; y: number } | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<{
    x: number; y: number; width: number; height: number;
  } | null>(null);
  const [selectedRegionEditId, setSelectedRegionEditId] = useState<number | null>(null);
  const [regionInstruction, setRegionInstruction] = useState('');

  // Add text edits
  const [addTextEdits, setAddTextEdits] = useState<AddTextEdit[]>([]);
  const [imageOverlayEdits, setImageOverlayEdits] = useState<ImageOverlayEdit[]>([]);
  const imageOverlayEditsRef = useRef<ImageOverlayEdit[]>([]);
  const [addTextPosition, setAddTextPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedAddTextId, setSelectedAddTextId] = useState<number | null>(null);
  const [addTextValue, setAddTextValue] = useState('');
  const [addTextFontSize, setAddTextFontSize] = useState<number>(24);
  const [addTextColor, setAddTextColor] = useState<string>('#000000');
  const [addTextFontFamily, setAddTextFontFamily] = useState<string>('Noto Sans JP');
  const [addTextShadow, setAddTextShadow] = useState<boolean>(true);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Add text marker dragging
  const [draggingAddTextId, setDraggingAddTextId] = useState<number | null>(null);
  const draggingAddTextIdRef = useRef<number | null>(null);
  const dragStartScreenRef = useRef<{ x: number; y: number } | null>(null);
  const dragOriginalPosRef = useRef<{ x: number; y: number } | null>(null);
  const dragMovedRef = useRef(false);
  const suppressClickRef = useRef(false);
  const dragUndoSnapshotRef = useRef<EditSnapshot | null>(null);

  // Undo/Redo
  const [undoStack, setUndoStack] = useState<EditSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<EditSnapshot[]>([]);

  // Applying
  const [isApplying, setIsApplying] = useState(false);
  const [applyStatus, setApplyStatus] = useState<string>('適用中...');
  const [error, setError] = useState<string | null>(null);

  // Refs
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const nextRegionIdRef = useRef(1);
  const nextAddTextIdRef = useRef(1);
  const nextImageOverlayIdRef = useRef(1);

  // Computed
  const changeCount = textEdits.length + regionEdits.length + addTextEdits.length + imageOverlayEdits.length;
  const hasChanges = changeCount > 0;
  const naturalPhotoEditCount = regionEdits.filter((edit) => edit.mode === 'naturalPhoto').length;

  useEffect(() => {
    imageOverlayEditsRef.current = imageOverlayEdits;
  }, [imageOverlayEdits]);

  // ---- Text detection ----
  const detectTextElements = useCallback(async () => {
    if (!image.url) return;
    setHasAttemptedTextDetection(true);
    setIsDetecting(true);
    setDetectError(null);
    try {
      const img = new Image();
      const dimensions = await new Promise<{ width: number; height: number }>(
        (resolve, reject) => {
          img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
          img.onerror = reject;
          img.src = image.url;
        }
      );
      const result = await imageGenerationService.extractTextElements({
        imageUrl: image.url,
        imageWidth: dimensions.width,
        imageHeight: dimensions.height,
      });
      if (result.success && result.data) {
        setTextElements(result.data.elements);
      } else {
        setDetectError('テキスト要素を検出できませんでした');
      }
    } catch (err) {
      console.error('Failed to detect text:', err);
      setDetectError('テキスト検出中にエラーが発生しました');
    } finally {
      setIsDetecting(false);
    }
  }, [image.url]);

  // Load Google Fonts (only once, check for existing link)
  useEffect(() => {
    const families = FONT_OPTIONS.map((f) => f.value.replace(/ /g, '+')).join('&family=');
    const href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
    const existing = document.head.querySelector(`link[href="${href}"]`);
    if (existing) {
      setFontsLoaded(true);
      return;
    }
    const link = document.createElement('link');
    link.href = href;
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    setFontsLoaded(true);
    // Don't remove on cleanup - fonts should persist across modal opens
  }, []);

  useEffect(() => {
    return () => {
      setTextElements([]);
      setHasAttemptedTextDetection(false);
      setTextEdits([]);
      setRegionEdits([]);
      setAddTextEdits([]);
      imageOverlayEditsRef.current.forEach((edit) => URL.revokeObjectURL(edit.previewUrl));
      setImageOverlayEdits([]);
      setUndoStack([]);
      setRedoStack([]);
      setSelectedElementId(null);
      setSelectedRegion(null);
      setAddTextPosition(null);
      setEditMode('region');
    };
  }, [isOpen]);

  // Focus textarea when popover opens
  useEffect(() => {
    if ((selectedElementId !== null || selectedRegion || addTextPosition || selectedAddTextId !== null) && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [selectedElementId, popoverTab, selectedRegion, addTextPosition, selectedAddTextId]);

  // Close popover on outside click (unified for all modes)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        const target = e.target as HTMLElement;
        if (target.closest('[data-text-element]')) return;
        if (target.closest('[data-region-edit]')) return;
        if (target.closest('[data-addtext-marker]')) return;
        setSelectedElementId(null);
        setSelectedRegion(null);
        setSelectedRegionEditId(null);
        setAddTextPosition(null);
        setSelectedAddTextId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close with confirmation if changes exist
  const handleCloseWithConfirm = useCallback(async () => {
    if (isApplying) return;
    if (hasChanges) {
      if (!(await confirm({ title: '編集内容が保存されていません。閉じますか？', tone: 'destructive', confirmText: '閉じる' }))) return;
    }
    onClose();
  }, [isApplying, hasChanges, onClose, confirm]);

  // Discard with confirmation
  const handleDiscardWithConfirm = async () => {
    if (!hasChanges) return;
    if (!(await confirm({ title: 'すべての変更を破棄しますか？', tone: 'destructive', confirmText: '破棄する' }))) return;
    handleDiscard();
  };

  // Close popovers on mode switch
  const switchMode = (mode: EditMode) => {
    setEditMode(mode);
    setSelectedElementId(null);
    setSelectedRegion(null);
    setSelectedRegionEditId(null);
    setAddTextPosition(null);
    setSelectedAddTextId(null);
    if (mode === 'text' && !hasAttemptedTextDetection && textElements.length === 0) {
      detectTextElements();
    }
  };

  const isSelectionMode = editMode === 'region' || editMode === 'naturalPhoto';

  // ---- Unified Undo/Redo ----
  const pushUndo = useCallback(() => {
    setUndoStack((prev) => [...prev, {
      textEdits: [...textEdits],
      regionEdits: [...regionEdits],
      addTextEdits: [...addTextEdits],
      imageOverlayEdits: [...imageOverlayEdits],
    }]);
    setRedoStack([]);
  }, [textEdits, regionEdits, addTextEdits, imageOverlayEdits]);

  const handleUndo = useCallback(() => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((r) => [...r, { textEdits: [...textEdits], regionEdits: [...regionEdits], addTextEdits: [...addTextEdits], imageOverlayEdits: [...imageOverlayEdits] }]);
    setTextEdits(prev.textEdits);
    setRegionEdits(prev.regionEdits);
    setAddTextEdits(prev.addTextEdits);
    setImageOverlayEdits(prev.imageOverlayEdits ?? []);
    setUndoStack((u) => u.slice(0, -1));
    setSelectedElementId(null);
    setSelectedRegion(null);
    setSelectedRegionEditId(null);
    setAddTextPosition(null);
    setSelectedAddTextId(null);
  }, [undoStack, textEdits, regionEdits, addTextEdits, imageOverlayEdits]);

  const handleRedo = useCallback(() => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((u) => [...u, { textEdits: [...textEdits], regionEdits: [...regionEdits], addTextEdits: [...addTextEdits], imageOverlayEdits: [...imageOverlayEdits] }]);
    setTextEdits(next.textEdits);
    setRegionEdits(next.regionEdits);
    setAddTextEdits(next.addTextEdits);
    setImageOverlayEdits(next.imageOverlayEdits ?? []);
    setRedoStack((r) => r.slice(0, -1));
    setSelectedElementId(null);
    setSelectedRegion(null);
    setSelectedRegionEditId(null);
    setAddTextPosition(null);
    setSelectedAddTextId(null);
  }, [redoStack, textEdits, regionEdits, addTextEdits, imageOverlayEdits]);

  const handleDiscard = () => {
    setTextEdits([]);
    setRegionEdits([]);
    setAddTextEdits([]);
    imageOverlayEdits.forEach((edit) => URL.revokeObjectURL(edit.previewUrl));
    setImageOverlayEdits([]);
    setUndoStack([]);
    setRedoStack([]);
    setSelectedElementId(null);
    setSelectedRegion(null);
    setSelectedRegionEditId(null);
    setAddTextPosition(null);
    setSelectedAddTextId(null);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      // Escape: close popover -> close modal (with confirmation)
      if (e.key === 'Escape') {
        if (selectedElementId !== null || selectedRegion || addTextPosition || selectedAddTextId !== null) {
          setSelectedElementId(null);
          setSelectedRegion(null);
          setSelectedRegionEditId(null);
          setAddTextPosition(null);
          setSelectedAddTextId(null);
        } else {
          handleCloseWithConfirm();
        }
        e.preventDefault();
        return;
      }

      // Ctrl+Z / Cmd+Z: Undo (not in text fields)
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        if ((e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).tagName === 'INPUT') return;
        handleUndo();
        e.preventDefault();
        return;
      }

      // Ctrl+Shift+Z / Cmd+Shift+Z or Ctrl+Y: Redo
      if (((e.ctrlKey || e.metaKey) && e.key === 'z' && e.shiftKey) || ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
        if ((e.target as HTMLElement).tagName === 'TEXTAREA' || (e.target as HTMLElement).tagName === 'INPUT') return;
        handleRedo();
        e.preventDefault();
        return;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedElementId, selectedRegion, addTextPosition, selectedAddTextId, handleUndo, handleRedo, handleCloseWithConfirm]);

  // Auto-dismiss error toast
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ---- Text Edit handlers ----
  const getCurrentText = (element: TextElement): string => {
    const edit = textEdits.find((e) => e.elementId === element.id);
    return edit ? edit.newText : element.text;
  };

  const handleElementClick = (element: TextElement) => {
    if (editMode !== 'text') return;
    setSelectedElementId(element.id);
    setPopoverTab('text');
    setPopoverText(getCurrentText(element));
    setAiPrompt('');
  };

  const handleSaveTextEdit = () => {
    if (selectedElementId === null) return;
    const element = textElements.find((e) => e.id === selectedElementId);
    if (!element) return;

    if (popoverTab === 'text') {
      if (popoverText === element.text) {
        pushUndo();
        setTextEdits((prev) => prev.filter((e) => e.elementId !== selectedElementId));
      } else if (popoverText !== getCurrentText(element)) {
        pushUndo();
        setTextEdits((prev) => {
          const existing = prev.find((e) => e.elementId === selectedElementId);
          if (existing) {
            return prev.map((e) =>
              e.elementId === selectedElementId ? { ...e, newText: popoverText } : e
            );
          }
          return [...prev, { elementId: selectedElementId, originalText: element.text, newText: popoverText }];
        });
      }
    } else if (popoverTab === 'ai' && aiPrompt.trim()) {
      pushUndo();
      setTextEdits((prev) => {
        const existing = prev.find((e) => e.elementId === selectedElementId);
        const instruction = `[AI: ${aiPrompt.trim()}]`;
        if (existing) {
          return prev.map((e) =>
            e.elementId === selectedElementId ? { ...e, newText: instruction } : e
          );
        }
        return [...prev, { elementId: selectedElementId, originalText: element.text, newText: instruction }];
      });
    }
    setSelectedElementId(null);
  };

  // ---- Region Edit handlers ----
  // Cache image rect during region-mode drag to avoid repeated getBoundingClientRect calls on mousemove.
  // Set on mouseDown (region mode only), cleared on mouseUp and mouseLeave.
  const cachedRectRef = useRef<DOMRect | null>(null);

  const getRelativeCoords = (e: React.MouseEvent) => {
    if (!imageRef.current) return null;
    const rect = cachedRectRef.current || imageRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    return { x, y };
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (isSelectionMode) {
      if ((e.target as HTMLElement).closest('[data-region-edit]')) return;
      if ((e.target as HTMLElement).closest('[data-popover]')) return;
      if (selectedRegion && !(e.target as HTMLElement).closest('[data-popover]')) {
        setSelectedRegion(null);
        setSelectedRegionEditId(null);
      }
      const coords = getRelativeCoords(e);
      if (!coords) return;
      // Cache rect at drag start for mousemove performance
      if (imageRef.current) cachedRectRef.current = imageRef.current.getBoundingClientRect();
      setIsDragging(true);
      setDragStart(coords);
      setDragCurrent(coords);
      e.preventDefault();
    }
  };

  // Use onClick for addText mode — more reliable than onMouseDown across browsers
  const handleClick = (e: React.MouseEvent) => {
    if (editMode === 'addText') {
      // Don't open new popover if we just finished dragging a marker
      if (suppressClickRef.current) return;
      if ((e.target as HTMLElement).closest('[data-addtext-marker]')) return;
      if ((e.target as HTMLElement).closest('[data-popover]')) return;
      const coords = getRelativeCoords(e);
      if (!coords) return;
      // Open new popover at clicked position (replaces any existing popover)
      setAddTextPosition(coords);
      setSelectedAddTextId(null);
      setAddTextValue('');
      setAddTextFontSize(24);
      setAddTextColor('#000000');
      setAddTextFontFamily('Noto Sans JP');
      setAddTextShadow(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isSelectionMode || !isDragging || !dragStart) return;
    const coords = getRelativeCoords(e);
    if (coords) setDragCurrent(coords);
  };

  const handleMouseUp = () => {
    if (!isSelectionMode || !isDragging || !dragStart || !dragCurrent) return;
    setIsDragging(false);
    cachedRectRef.current = null;
    const x = Math.min(dragStart.x, dragCurrent.x);
    const y = Math.min(dragStart.y, dragCurrent.y);
    const width = Math.abs(dragCurrent.x - dragStart.x);
    const height = Math.abs(dragCurrent.y - dragStart.y);
    setDragStart(null);
    setDragCurrent(null);
    if (width < 0.02 || height < 0.02) return;
    setSelectedRegion({ x, y, width, height });
    setSelectedRegionEditId(null);
    setRegionInstruction('');
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const handleRegionEditClick = (edit: RegionEdit) => {
    setSelectedRegion(edit.region);
    setSelectedRegionEditId(edit.id);
    setRegionInstruction(edit.instruction);
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  const handleSaveRegion = () => {
    if (!selectedRegion || !regionInstruction.trim()) return;
    pushUndo();
    if (selectedRegionEditId !== null) {
      setRegionEdits((prev) =>
        prev.map((e) => e.id === selectedRegionEditId ? { ...e, instruction: regionInstruction.trim() } : e)
      );
    } else {
      const newEdit: RegionEdit = {
        id: nextRegionIdRef.current++,
        region: selectedRegion,
        instruction: regionInstruction.trim(),
      };
      setRegionEdits((prev) => [...prev, newEdit]);
    }
    setSelectedRegion(null);
    setSelectedRegionEditId(null);
    setRegionInstruction('');
  };

  const handleAddNaturalPhotoEdit = (region?: RegionEdit['region']) => {
    pushUndo();
    const targetRegion = region ?? { x: 0, y: 0, width: 1, height: 1 };
    setRegionEdits((prev) => [
      ...prev,
      {
        id: nextRegionIdRef.current++,
        region: targetRegion,
        instruction: NATURAL_PHOTO_INSTRUCTION,
        mode: 'naturalPhoto',
      },
    ]);
    setSelectedRegion(null);
    setSelectedRegionEditId(null);
    setRegionInstruction('');
  };

  const handleDeleteRegion = () => {
    if (selectedRegionEditId === null) return;
    pushUndo();
    setRegionEdits((prev) => prev.filter((e) => e.id !== selectedRegionEditId));
    setSelectedRegion(null);
    setSelectedRegionEditId(null);
  };

  const handleImageOverlayUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !selectedRegion) return;
    if (!file.type.startsWith('image/')) {
      setError('画像ファイルを選択してください');
      return;
    }

    try {
      const imageData = await imageGenerationService.fileToBase64(file);
      const previewUrl = URL.createObjectURL(file);
      pushUndo();
      setImageOverlayEdits((prev) => [
        ...prev,
        {
          id: nextImageOverlayIdRef.current++,
          region: selectedRegion,
          imageData,
          previewUrl,
          fileName: file.name,
        },
      ]);
      setSelectedRegion(null);
      setSelectedRegionEditId(null);
      setRegionInstruction('');
    } catch (err) {
      console.error('Failed to read overlay image:', err);
      setError('アップロード画像の読み込みに失敗しました');
    }
  };

  const handleDeleteImageOverlay = (id: number) => {
    pushUndo();
    setImageOverlayEdits((prev) => prev.filter((edit) => edit.id !== id));
  };

  // ---- Add Text handlers ----
  const handleAddTextMarkerClick = (edit: AddTextEdit) => {
    setSelectedAddTextId(edit.id);
    setAddTextPosition(edit.position);
    setAddTextValue(edit.text);
    setAddTextFontSize(edit.fontSize);
    setAddTextColor(edit.color);
    setAddTextFontFamily(edit.fontFamily);
    setAddTextShadow(edit.textShadow ?? true);
    setTimeout(() => textareaRef.current?.focus(), 100);
  };

  // Drag-to-move for addText markers
  const handleAddTextMarkerMouseDown = (e: React.MouseEvent, edit: AddTextEdit) => {
    e.stopPropagation();
    e.preventDefault();
    draggingAddTextIdRef.current = edit.id;
    setDraggingAddTextId(edit.id);
    dragStartScreenRef.current = { x: e.clientX, y: e.clientY };
    dragOriginalPosRef.current = { x: edit.position.x, y: edit.position.y };
    dragMovedRef.current = false;
    // Save undo snapshot before drag starts (with current position)
    dragUndoSnapshotRef.current = {
      textEdits: [...textEdits],
      regionEdits: [...regionEdits],
      addTextEdits: [...addTextEdits],
      imageOverlayEdits: [...imageOverlayEdits],
    };
  };

  const handleAddTextDragMove = useCallback((clientX: number, clientY: number) => {
    const activeDragId = draggingAddTextIdRef.current;
    if (activeDragId === null || !dragStartScreenRef.current || !dragOriginalPosRef.current || !imageRef.current) return;
    const imgRect = imageRef.current.getBoundingClientRect();
    const dx = (clientX - dragStartScreenRef.current.x) / imgRect.width;
    const dy = (clientY - dragStartScreenRef.current.y) / imgRect.height;
    // Check if moved beyond threshold (5px)
    const pxDist = Math.sqrt(
      Math.pow(clientX - dragStartScreenRef.current.x, 2) +
      Math.pow(clientY - dragStartScreenRef.current.y, 2)
    );
    if (pxDist > 5) dragMovedRef.current = true;
    if (!dragMovedRef.current) return;
    const newX = Math.max(0, Math.min(1, dragOriginalPosRef.current.x + dx));
    const newY = Math.max(0, Math.min(1, dragOriginalPosRef.current.y + dy));
    setAddTextEdits((prev) =>
      prev.map((item) => item.id === activeDragId ? { ...item, position: { x: newX, y: newY } } : item)
    );
  }, []);

  const handleAddTextDragEnd = () => {
    const activeDragId = draggingAddTextIdRef.current;
    if (activeDragId === null) return;
    const editItem = addTextEdits.find((e) => e.id === activeDragId);
    if (!dragMovedRef.current && editItem) {
      // Not moved — treat as click → open popover for re-edit
      handleAddTextMarkerClick(editItem);
    } else if (dragMovedRef.current) {
      // Actually moved — push pre-drag snapshot to undo stack
      const snapshot = dragUndoSnapshotRef.current;
      if (snapshot) {
        setUndoStack((prev) => [...prev, snapshot]);
        setRedoStack([]);
      }
      // Suppress the click event that follows mouseup
      suppressClickRef.current = true;
      setTimeout(() => { suppressClickRef.current = false; }, 0);
    }
    draggingAddTextIdRef.current = null;
    setDraggingAddTextId(null);
    dragStartScreenRef.current = null;
    dragOriginalPosRef.current = null;
    dragMovedRef.current = false;
    dragUndoSnapshotRef.current = null;
  };

  useEffect(() => {
    if (draggingAddTextId === null) return;

    const handleWindowMouseMove = (event: MouseEvent) => {
      handleAddTextDragMove(event.clientX, event.clientY);
    };
    const handleWindowMouseUp = () => {
      handleAddTextDragEnd();
    };

    window.addEventListener('mousemove', handleWindowMouseMove);
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleWindowMouseMove);
      window.removeEventListener('mouseup', handleWindowMouseUp);
    };
  }, [draggingAddTextId, handleAddTextDragMove, addTextEdits]);

  const handleSaveAddText = () => {
    if (!addTextPosition || !addTextValue.trim()) return;
    pushUndo();
    if (selectedAddTextId !== null) {
      setAddTextEdits((prev) =>
        prev.map((e) => e.id === selectedAddTextId
          ? { ...e, text: addTextValue.trim(), fontSize: addTextFontSize, color: addTextColor, fontFamily: addTextFontFamily, textShadow: addTextShadow }
          : e
        )
      );
    } else {
      const newEdit: AddTextEdit = {
        id: nextAddTextIdRef.current++,
        position: addTextPosition,
        text: addTextValue.trim(),
        fontSize: addTextFontSize,
        color: addTextColor,
        fontFamily: addTextFontFamily,
        textShadow: addTextShadow,
      };
      setAddTextEdits((prev) => [...prev, newEdit]);
    }
    setAddTextPosition(null);
    setSelectedAddTextId(null);
    setAddTextValue('');
  };

  const handleDeleteAddText = () => {
    if (selectedAddTextId === null) return;
    pushUndo();
    setAddTextEdits((prev) => prev.filter((e) => e.id !== selectedAddTextId));
    setAddTextPosition(null);
    setSelectedAddTextId(null);
  };

  const getNaturalImageSize = () => {
    const img = imageRef.current;
    if (!img?.naturalWidth || !img?.naturalHeight) return null;
    return { width: img.naturalWidth, height: img.naturalHeight };
  };

  const toPixelRegion = (
    region: RegionEdit['region'],
    naturalSize: { width: number; height: number } | null = getNaturalImageSize()
  ): PixelRegion | null => {
    if (!naturalSize) return null;

    const left = Math.max(0, Math.min(naturalSize.width - 1, Math.round(region.x * naturalSize.width)));
    const top = Math.max(0, Math.min(naturalSize.height - 1, Math.round(region.y * naturalSize.height)));
    const right = Math.max(left + 1, Math.min(naturalSize.width, Math.round((region.x + region.width) * naturalSize.width)));
    const bottom = Math.max(top + 1, Math.min(naturalSize.height, Math.round((region.y + region.height) * naturalSize.height)));

    return {
      left,
      top,
      right,
      bottom,
      width: right - left,
      height: bottom - top,
    };
  };

  const createRegionMask = async (regions: RegionEdit['region'][]): Promise<string | undefined> => {
    const naturalSize = getNaturalImageSize();
    if (!naturalSize || regions.length === 0) return undefined;

    const canvas = document.createElement('canvas');
    canvas.width = naturalSize.width;
    canvas.height = naturalSize.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    ctx.clearRect(0, 0, naturalSize.width, naturalSize.height);
    ctx.fillStyle = 'rgba(0, 0, 0, 1)';
    ctx.fillRect(0, 0, naturalSize.width, naturalSize.height);

    regions.forEach((region) => {
      const pixelRegion = toPixelRegion(region, naturalSize);
      if (!pixelRegion) return;
      ctx.clearRect(pixelRegion.left, pixelRegion.top, pixelRegion.width, pixelRegion.height);
    });

    return canvas.toDataURL('image/png').split(',')[1];
  };

  // ---- Apply ----
  const handleApply = async () => {
    if (!hasChanges) return;
    setIsApplying(true);
    setApplyStatus('編集を開始しています...');
    setError(null);

    try {
      let editPrompt = 'この広告バナー画像を以下のように編集してください：\n\n';
      const hasAiEdits = textEdits.length > 0 || regionEdits.length > 0;
      const hasDirectCompositeEdits = addTextEdits.length > 0 || imageOverlayEdits.length > 0;
      const hasNaturalPhotoEdits = naturalPhotoEditCount > 0;
      const editMetadata = hasNaturalPhotoEdits ? {
        edit_mode: 'natural_photo',
        natural_photo_mode: true,
        natural_photo_region_count: naturalPhotoEditCount,
        natural_photo_scope: regionEdits.some((edit) => edit.mode === 'naturalPhoto' && edit.region.width >= 0.99 && edit.region.height >= 0.99)
          ? 'whole_image'
          : 'selected_region',
      } : undefined;
      const naturalSize = getNaturalImageSize();
      const maskImageData = regionEdits.length > 0
        ? await createRegionMask(regionEdits.map((edit) => edit.region))
        : undefined;

      // Text edits
      if (textEdits.length > 0) {
        editPrompt += '【テキスト変更】\n';
        textEdits.forEach((edit) => {
          if (edit.newText.startsWith('[AI: ')) {
            const instruction = edit.newText.slice(5, -1);
            editPrompt += `・「${edit.originalText}」について：${instruction}\n`;
          } else {
            editPrompt += `・「${edit.originalText}」→「${edit.newText}」\n`;
          }
        });
        editPrompt += '\n';
      }

      // Region edits (with improved prompt)
      if (regionEdits.length > 0) {
        editPrompt += '【範囲指定の編集】\n';
        if (naturalSize) {
          editPrompt += `※元画像サイズは ${naturalSize.width}x${naturalSize.height}px です。添付されたマスク画像の透明部分だけを編集対象にしてください。\n`;
        } else {
          editPrompt += '※添付されたマスク画像の透明部分だけを編集対象にしてください。\n';
        }
        editPrompt += '※範囲外は絶対に変更しないでください。範囲内も単色で塗りつぶさず、広告デザインの調和を保ってください。\n';
        regionEdits.forEach((edit) => {
          const { x, y, width, height } = edit.region;
          const left = Math.round(x * 100);
          const top = Math.round(y * 100);
          const right = Math.round((x + width) * 100);
          const bottom = Math.round((y + height) * 100);
          const pixelRegion = toPixelRegion(edit.region, naturalSize);
          const pixelText = pixelRegion
            ? ` / px: x=${pixelRegion.left}, y=${pixelRegion.top}, w=${pixelRegion.width}, h=${pixelRegion.height}`
            : '';
          editPrompt += `・左${left}%上${top}%〜右${right}%下${bottom}%${pixelText}：${edit.instruction}\n`;
        });
        editPrompt += '\n';
      }

      if (hasAiEdits) {
        editPrompt += '【共通ルール】\n';
        editPrompt += '・広告バナーとしてプロのデザイナーが作ったような自然な仕上がりにすること\n';
        editPrompt += '・指示されていない部分は変更しないこと\n';
      } else {
        editPrompt = '画像上にテキスト・アップロード画像を直接合成';
      }

      let result = hasAiEdits
        ? await imageGenerationService.editImageAsync(
            editPrompt,
            undefined,
            image.url,
            undefined,
            image.generation_source || 'image_generation',
            'openai',
            image.id > 0 ? image.id : undefined,
            maskImageData,
            (job) => {
              if (job.status === 'pending') {
                setApplyStatus('AI編集を待機しています...');
              } else if (job.status === 'processing') {
                setApplyStatus(`AI編集中... ${job.progress || 0}%`);
              } else if (job.status === 'completed') {
                setApplyStatus('編集結果を反映しています...');
              }
            },
            editMetadata
          )
        : null;

      if (hasDirectCompositeEdits) {
        setApplyStatus('テキスト・画像を合成しています...');
        const compositeBaseUrl = result?.data?.url || image.url;
        const compositeSourceImageId = result?.data?.id || (image.id > 0 ? image.id : undefined);
        result = await imageGenerationService.compositeImage({
          imageUrl: compositeBaseUrl,
          texts: addTextEdits.map((edit) => ({
            text: edit.text,
            x: edit.position.x,
            y: edit.position.y,
            font_size: edit.fontSize,
            color: edit.color,
            font_family: edit.fontFamily,
            text_shadow: edit.textShadow,
          })),
          imageOverlays: imageOverlayEdits.map((edit) => ({
            image_data: edit.imageData,
            region: edit.region,
          })),
          generationSource: image.generation_source || 'image_generation',
          sourceImageId: compositeSourceImageId,
          metadata: editMetadata,
        });
      }

      if (result?.success && result.data) {
        await onApply({
          ...image,
          id: result.data.id,
          url: result.data.url || '',
          s3_key: result.data.s3_key,
          source_image_id: result.data.source_image_id,
          prompt: editPrompt,
          created_at: result.data.created_at,
          file_size: result.data.file_size,
        });
        onClose();
      }
    } catch (err) {
      console.error('Failed to apply edits:', err);
      const message = (err as any)?.response?.data?.error
        || (err as any)?.response?.data?.message
        || (err as any)?.message
        || '編集の適用に失敗しました';
      setError(message);
    } finally {
      setIsApplying(false);
      setApplyStatus('適用中...');
    }
  };

  // ---- Popover positioning ----
  const getTextPopoverPosition = (element: TextElement) => {
    if (!containerRef.current || !imageRef.current) return { top: 0, left: 0 };
    const imgRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const imgOffsetX = imgRect.left - containerRect.left;
    const imgOffsetY = imgRect.top - containerRect.top;
    const elemCenterX = imgOffsetX + element.x * imgRect.width + (element.width * imgRect.width) / 2;
    const elemTop = imgOffsetY + element.y * imgRect.height;
    const elemBottom = imgOffsetY + (element.y + element.height) * imgRect.height;
    const popoverWidth = 320;
    const popoverHeight = 200;
    let left = elemCenterX - popoverWidth / 2;
    const maxLeft = containerRect.width - popoverWidth - 8;
    left = Math.max(8, Math.min(left, maxLeft));
    const spaceBelow = imgRect.bottom - containerRect.top - elemBottom;
    const bottomToolbarMargin = 80;
    if (spaceBelow < popoverHeight + bottomToolbarMargin) {
      return { top: Math.max(0, elemTop - popoverHeight - 8), left };
    }
    return { top: elemBottom + 8, left };
  };

  const getRegionPopoverPosition = () => {
    if (!selectedRegion || !imageRef.current || !containerRef.current) return { top: 0, left: 0 };
    const imgRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const imgOffsetX = imgRect.left - containerRect.left;
    const imgOffsetY = imgRect.top - containerRect.top;
    const regionCenterX = imgOffsetX + (selectedRegion.x + selectedRegion.width / 2) * imgRect.width;
    const regionBottom = imgOffsetY + (selectedRegion.y + selectedRegion.height) * imgRect.height;
    const regionTop = imgOffsetY + selectedRegion.y * imgRect.height;
    const popoverWidth = 320;
    const popoverHeight = 180;
    let left = regionCenterX - popoverWidth / 2;
    const maxLeft = containerRect.width - popoverWidth - 8;
    left = Math.max(8, Math.min(left, maxLeft));
    const spaceBelow = imgRect.bottom - containerRect.top - regionBottom;
    if (spaceBelow < popoverHeight + 80) {
      return { top: Math.max(0, regionTop - popoverHeight - 8), left };
    }
    return { top: regionBottom + 8, left };
  };

  const getAddTextPopoverPosition = () => {
    const pos = addTextPosition;
    if (!pos || !imageRef.current || !containerRef.current) return { top: 0, left: 0, width: 320 };
    const imgRect = imageRef.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const imgOffsetX = imgRect.left - containerRect.left;
    const imgOffsetY = imgRect.top - containerRect.top;
    const clickX = imgOffsetX + pos.x * imgRect.width;
    const clickY = imgOffsetY + pos.y * imgRect.height;
    const preferredPopoverWidth = 320;
    const minPopoverWidth = 260;
    const popoverHeight = 400;
    const sideGap = 16;
    const viewportPadding = 8;

    const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));
    const verticalTop = clamp(clickY - popoverHeight / 2, 8, Math.max(8, containerRect.height - popoverHeight - 8));
    const rightSpace = window.innerWidth - imgRect.right;
    const leftSpace = imgRect.left;
    const sideWidth = (space: number) => Math.min(preferredPopoverWidth, Math.max(minPopoverWidth, space - sideGap - viewportPadding));

    if (rightSpace >= minPopoverWidth + sideGap + viewportPadding) {
      return { top: verticalTop, left: imgOffsetX + imgRect.width + sideGap, width: sideWidth(rightSpace) };
    }

    if (leftSpace >= minPopoverWidth + sideGap + viewportPadding) {
      const width = sideWidth(leftSpace);
      return { top: verticalTop, left: imgOffsetX - width - sideGap, width };
    }

    const centeredLeft = clamp(clickX - preferredPopoverWidth / 2, 8, Math.max(8, containerRect.width - preferredPopoverWidth - 8));
    const bottomSpace = window.innerHeight - imgRect.bottom;
    if (bottomSpace >= popoverHeight + 104) {
      return { top: imgOffsetY + imgRect.height + 16, left: centeredLeft, width: preferredPopoverWidth };
    }

    if (imgRect.top >= popoverHeight + 64) {
      return { top: imgOffsetY - popoverHeight - 16, left: centeredLeft, width: preferredPopoverWidth };
    }

    return { top: Math.max(8, clickY + 16), left: centeredLeft, width: preferredPopoverWidth };
  };

  // ---- Cursor ----
  const getCursor = () => {
    if (draggingAddTextId !== null) return 'grabbing';
    if (editMode === 'addText') return 'text';
    return 'crosshair';
  };

  // ---- Mode-specific hint text ----
  const getHintText = () => {
    switch (editMode) {
      case 'text': return '① 文字を選ぶ → ② 内容を変更して「変更を追加」 → ③ 画像に反映して保存';
      case 'region': return '① 直したい範囲をドラッグ → ② 修正内容を入力して「変更を追加」 → ③ 画像に反映して保存';
      case 'addText': return '① 文字を置く位置をクリック → ② 文字を入力して「変更を追加」 → ③ 画像に反映して保存';
      case 'naturalPhoto': return '① 範囲をドラッグ、または「全体に適用」 → ② 変更を追加 → ③ 画像に反映して保存';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-70"
        onClick={handleCloseWithConfirm}
      />

      {/* Main container */}
      <div className="relative flex flex-col items-center justify-center w-full h-full px-3 pt-24 pb-40 sm:px-8 sm:pb-28">
        {/* Close button */}
        <button
          onClick={handleCloseWithConfirm}
          className="absolute top-4 right-4 z-10 p-2.5 text-white hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors shadow-lg"
          title="閉じる (Esc)"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Hint */}
        <div className="absolute top-4 left-4 right-16 z-10 mx-auto max-w-3xl px-4 py-2 bg-black/60 text-white/90 rounded-lg text-xs sm:text-sm flex items-center gap-2">
          {editMode === 'text' && <Type className="w-4 h-4" />}
          {editMode === 'region' && <MousePointer2 className="w-4 h-4" />}
          {editMode === 'addText' && <Plus className="w-4 h-4" />}
          {editMode === 'naturalPhoto' && <Camera className="w-4 h-4" />}
          {getHintText()}
        </div>

        {/* Image area */}
        {isDetecting && editMode === 'text' ? (
          <div className="flex flex-col items-center gap-3 text-white">
            <Loader2 className="w-10 h-10 animate-spin text-indigo-400" />
            <p className="text-sm font-medium">テキスト要素を検出中...</p>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="relative inline-block select-none"
            style={{ cursor: getCursor() }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onClick={handleClick}
            onMouseLeave={() => {
              if (isDragging) {
                setIsDragging(false);
                setDragStart(null);
                setDragCurrent(null);
                cachedRectRef.current = null;
              }
            }}
          >
            <img
              ref={imageRef}
              src={image.url}
              alt="Banner"
              className="max-w-full max-h-[calc(100vh-180px)] object-contain rounded-lg shadow-2xl"
              draggable={false}
            />

            {/* Text detection inline error/loading for text mode */}
            {editMode === 'text' && isDetecting && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg z-10">
                <div className="flex flex-col items-center gap-2 text-white">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
                  <p className="text-sm">テキスト検出中...</p>
                </div>
              </div>
            )}
            {editMode === 'text' && !isDetecting && detectError && textElements.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg z-10">
                <div className="flex flex-col items-center gap-2 text-white">
                  <p className="text-sm">{detectError}</p>
                  <button
                    onClick={detectTextElements}
                    className="px-3 py-1.5 text-xs bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                  >
                    再試行
                  </button>
                </div>
              </div>
            )}

            {/* Text element overlays (visible in all modes, clickable in text mode) */}
            {textElements.map((element) => {
              const isSelected = selectedElementId === element.id;
              const isHovered = hoveredElementId === element.id;
              const isEdited = textEdits.some((e) => e.elementId === element.id);
              const isTextMode = editMode === 'text';

              return (
                <div
                  key={element.id}
                  data-text-element
                  className={`absolute transition-all duration-150 rounded-sm ${
                    isTextMode
                      ? isSelected
                        ? 'ring-2 ring-blue-500 bg-blue-500/10'
                        : isHovered
                          ? 'ring-2 ring-blue-400 bg-blue-400/10'
                          : isEdited
                            ? 'ring-2 ring-green-400 bg-green-400/10'
                            : 'ring-1 ring-blue-300/60 bg-blue-200/5 hover:ring-blue-400 hover:bg-blue-400/10'
                      : isEdited
                        ? 'ring-2 ring-green-400 bg-green-400/10'
                        : 'ring-0'
                  }`}
                  style={{
                    left: `${element.x * 100}%`,
                    top: `${element.y * 100}%`,
                    width: `${element.width * 100}%`,
                    height: `${element.height * 100}%`,
                    cursor: isTextMode ? 'pointer' : 'inherit',
                    pointerEvents: isTextMode ? 'auto' : 'none',
                  }}
                  onClick={() => handleElementClick(element)}
                  onMouseEnter={() => isTextMode && setHoveredElementId(element.id)}
                  onMouseLeave={() => setHoveredElementId(null)}
                >
                  {isEdited && !isSelected && (
                    <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}
                </div>
              );
            })}

            {/* Region edit overlays */}
            {regionEdits.map((edit) => {
              const isSelected = selectedRegionEditId === edit.id;
              return (
                <div
                  key={`region-${edit.id}`}
                  data-region-edit
                  className={`absolute rounded-sm cursor-pointer transition-all duration-150 ${
                    isSelected
                      ? 'ring-2 ring-blue-500 bg-blue-500/15'
                      : 'ring-2 ring-green-400 bg-green-400/10 hover:ring-green-500 hover:bg-green-400/15'
                  }`}
                  style={{
                    left: `${edit.region.x * 100}%`,
                    top: `${edit.region.y * 100}%`,
                    width: `${edit.region.width * 100}%`,
                    height: `${edit.region.height * 100}%`,
                    pointerEvents: isSelectionMode ? 'auto' : 'none',
                  }}
                  onClick={(e) => { e.stopPropagation(); handleRegionEditClick(edit); }}
                >
                  <div className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs px-1.5 py-0.5 truncate rounded-b-sm">
                    {edit.mode === 'naturalPhoto' ? '自然写真化' : edit.instruction}
                  </div>
                </div>
              );
            })}

            {/* Uploaded image overlays */}
            {imageOverlayEdits.map((edit) => (
              <div
                key={`image-overlay-${edit.id}`}
                className="absolute rounded-sm ring-2 ring-emerald-400 bg-emerald-400/10 overflow-hidden"
                style={{
                  left: `${edit.region.x * 100}%`,
                  top: `${edit.region.y * 100}%`,
                  width: `${edit.region.width * 100}%`,
                  height: `${edit.region.height * 100}%`,
                  zIndex: 14,
                  pointerEvents: isSelectionMode ? 'auto' : 'none',
                }}
              >
                <img src={edit.previewUrl} alt={edit.fileName} className="w-full h-full object-cover" draggable={false} />
                {isSelectionMode && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleDeleteImageOverlay(edit.id); }}
                    className="absolute top-1 right-1 p-1 bg-black/60 hover:bg-black/80 text-white rounded-full"
                    title="アップロード画像を削除"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}

            {/* Add text markers - rendered with actual font/size/color */}
            {addTextEdits.map((edit, index) => {
              const isSelected = selectedAddTextId === edit.id;
              const isBeingDragged = draggingAddTextId === edit.id;
              return (
                <div
                  key={`addtext-${edit.id}`}
                  data-addtext-marker
                  className={`absolute transition-all duration-150 ${
                    isBeingDragged
                      ? 'ring-2 ring-purple-500 ring-offset-1 rounded opacity-80 cursor-grabbing'
                      : isSelected
                        ? 'ring-2 ring-purple-400 ring-offset-1 rounded cursor-grab'
                        : 'hover:ring-1 hover:ring-purple-300 hover:rounded border border-dashed border-transparent hover:border-purple-300/50 cursor-grab'
                  }`}
                  style={{
                    left: `${edit.position.x * 100}%`,
                    top: `${edit.position.y * 100}%`,
                    zIndex: isBeingDragged ? 22 : isSelected ? 21 : 15 + index,
                    maxWidth: `${Math.max(8, (1 - edit.position.x) * 100)}%`,
                    fontFamily: `'${edit.fontFamily}', sans-serif`,
                    fontSize: `${edit.fontSize}px`,
                    color: edit.color,
                    lineHeight: 1.4,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word' as const,
                    textShadow: edit.textShadow ? '0 1px 3px rgba(0,0,0,0.5)' : 'none',
                    pointerEvents: editMode === 'addText' ? 'auto' : 'none',
                    padding: '2px 4px',
                  }}
                  onMouseDown={(e) => handleAddTextMarkerMouseDown(e, edit)}
                >
                  {edit.text}
                </div>
              );
            })}

            {/* Drag rectangle */}
            {isDragging && dragStart && dragCurrent && (
              <div
                className="absolute border-2 border-dashed border-blue-400 bg-blue-400/15 pointer-events-none rounded-sm"
                style={{
                  left: `${Math.min(dragStart.x, dragCurrent.x) * 100}%`,
                  top: `${Math.min(dragStart.y, dragCurrent.y) * 100}%`,
                  width: `${Math.abs(dragCurrent.x - dragStart.x) * 100}%`,
                  height: `${Math.abs(dragCurrent.y - dragStart.y) * 100}%`,
                }}
              />
            )}

            {/* New selection rectangle */}
            {selectedRegion && selectedRegionEditId === null && isSelectionMode && (
              <div
                className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-none rounded-sm"
                style={{
                  left: `${selectedRegion.x * 100}%`,
                  top: `${selectedRegion.y * 100}%`,
                  width: `${selectedRegion.width * 100}%`,
                  height: `${selectedRegion.height * 100}%`,
                }}
              />
            )}

            {/* Add text live preview (before save) */}
            {addTextPosition && selectedAddTextId === null && editMode === 'addText' && addTextValue.trim() && (
              <div
                className="absolute pointer-events-none opacity-80"
                style={{
                  left: `${addTextPosition.x * 100}%`,
                  top: `${addTextPosition.y * 100}%`,
                  zIndex: 16,
                  maxWidth: `${Math.max(8, (1 - addTextPosition.x) * 100)}%`,
                  fontFamily: `'${addTextFontFamily}', sans-serif`,
                  fontSize: `${addTextFontSize}px`,
                  color: addTextColor,
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word' as const,
                  textShadow: addTextShadow ? '0 1px 3px rgba(0,0,0,0.5)' : 'none',
                }}
              >
                {addTextValue}
              </div>
            )}

            {/* Text edit popover */}
            {editMode === 'text' && selectedElementId !== null && (() => {
              const element = textElements.find((e) => e.id === selectedElementId);
              if (!element) return null;
              const pos = getTextPopoverPosition(element);
              return (
                <div
                  ref={popoverRef}
                  data-popover
                  className="absolute z-40 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-visible"
                  style={{ top: pos.top, left: pos.left, width: 320 }}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex border-b border-gray-200">
                    <button
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors ${
                        popoverTab === 'text'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      onClick={() => { setPopoverTab('text'); setPopoverText(getCurrentText(element)); }}
                    >
                      <Type className="w-4 h-4" />
                      テキスト編集
                    </button>
                    <button
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium transition-colors ${
                        popoverTab === 'ai'
                          ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                      onClick={() => setPopoverTab('ai')}
                    >
                      <MessageSquare className="w-4 h-4" />
                      AI編集
                    </button>
                  </div>
                  <div className="p-3">
                    {popoverTab === 'text' ? (
                      <textarea
                        ref={textareaRef}
                        className="w-full p-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
                        rows={3}
                        value={popoverText}
                        onChange={(e) => setPopoverText(e.target.value)}
                        placeholder="テキストを入力..."
                      />
                    ) : (
                      <textarea
                        ref={textareaRef}
                        className="w-full p-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
                        rows={3}
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="どのように変更しますか？"
                      />
                    )}
                  </div>
                  <div className="px-3 pb-3">
                    <button
                      onClick={handleSaveTextEdit}
                      className="w-full py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                    >
                      変更を追加
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Region edit popover */}
            {editMode === 'region' && selectedRegion && (() => {
              const pos = getRegionPopoverPosition();
              return (
                <div
                  ref={popoverRef}
                  data-popover
                  className="absolute z-40 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                  style={{ top: pos.top, left: pos.left }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                    <Pencil className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {selectedRegionEditId !== null ? '編集を修正' : '選択範囲を編集'}
                    </span>
                  </div>
	                  <div className="p-3">
	                    <textarea
	                      ref={textareaRef}
	                      className="w-full p-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent bg-gray-50"
	                      rows={3}
	                      value={regionInstruction}
	                      onChange={(e) => setRegionInstruction(e.target.value)}
	                      placeholder="例: ロゴを赤に変更 / テキストを「新商品」に変更 / 背景をぼかす"
	                    />
	                    {selectedRegionEditId === null && (
	                      <label className="mt-3 flex items-center justify-center gap-2 w-full py-2 text-sm font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg cursor-pointer transition-colors">
	                        <ImagePlus className="w-4 h-4" />
	                        この範囲に画像を配置
	                        <input
	                          type="file"
	                          accept="image/*"
	                          className="hidden"
	                          onChange={handleImageOverlayUpload}
	                        />
	                      </label>
	                    )}
	                  </div>
                  <div className="px-3 pb-3 flex gap-2">
                    {selectedRegionEditId !== null && (
                      <button
                        onClick={handleDeleteRegion}
                        className="flex-shrink-0 py-2 px-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        削除
                      </button>
                    )}
                    <button
                      onClick={handleSaveRegion}
                      disabled={!regionInstruction.trim()}
                      className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                        regionInstruction.trim()
                          ? 'text-white bg-blue-600 hover:bg-blue-700'
                          : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                      }`}
                    >
                      変更を追加
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Natural photo popover */}
            {editMode === 'naturalPhoto' && selectedRegion && selectedRegionEditId === null && (() => {
              const pos = getRegionPopoverPosition();
              return (
                <div
                  ref={popoverRef}
                  data-popover
                  className="absolute z-40 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                  style={{ top: pos.top, left: pos.left }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                    <Camera className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm font-medium text-gray-700">選択範囲を自然写真化</span>
                  </div>
                  <div className="p-3">
                    <p className="text-sm text-gray-600 leading-relaxed">
                      iPhoneで撮ったような自然光・生活感のある質感に整えます。
                    </p>
                  </div>
                  <div className="px-3 pb-3 flex gap-2">
                    <button
                      onClick={() => setSelectedRegion(null)}
                      className="flex-shrink-0 py-2 px-3 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                      取消
                    </button>
                    <button
                      onClick={() => handleAddNaturalPhotoEdit(selectedRegion)}
                      className="flex-1 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
                    >
                      変更を追加
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Add text popover */}
            {editMode === 'addText' && (addTextPosition || selectedAddTextId !== null) && (() => {
              const pos = getAddTextPopoverPosition();
              return (
                <div
                  ref={popoverRef}
                  data-popover
                  className="absolute z-40 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
                  style={{ top: pos.top, left: pos.left, width: pos.width }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 bg-gray-50">
                    <Plus className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium text-gray-700">
                      {selectedAddTextId !== null ? 'テキストを編集' : 'テキストを追加'}
                    </span>
                  </div>
                  <div className="p-3 space-y-3">
                    <textarea
                      ref={textareaRef}
                      className="w-full p-2.5 text-sm border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent bg-gray-50"
                      rows={2}
                      value={addTextValue}
                      onChange={(e) => setAddTextValue(e.target.value)}
                      placeholder="追加するテキストを入力..."
                    />
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-gray-500 mb-1 block">フォント</label>
                        <TaskySelect
                          value={addTextFontFamily}
                          onChange={(e) => setAddTextFontFamily(e.target.value)}
                          className="h-8 text-sm"
                          style={{ fontFamily: `'${addTextFontFamily}', sans-serif` }}
                        >
                          {FONT_OPTIONS.map((font) => (
                            <option key={font.value} value={font.value} style={{ fontFamily: `'${font.value}', sans-serif` }}>
                              {font.label} ({font.value})
                            </option>
                          ))}
                        </TaskySelect>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 mb-1 block">サイズ (px)</label>
                          <input
                            type="number"
                            min={8}
                            max={120}
                            value={addTextFontSize}
                            onChange={(e) => setAddTextFontSize(Math.max(8, Math.min(120, Number(e.target.value) || 8)))}
                            className="w-full px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400"
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs text-gray-500 mb-1 block">色</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={addTextColor}
                              onChange={(e) => setAddTextColor(e.target.value)}
                              className="w-8 h-8 rounded cursor-pointer border border-gray-200"
                            />
                            <input
                              type="text"
                              value={addTextColor}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) setAddTextColor(v);
                              }}
                              className="flex-1 px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400 font-mono"
                              placeholder="#ffffff"
                            />
                          </div>
                        </div>
                      </div>
                      <label className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
                        <span>ドロップシャドウ</span>
                        <input
                          type="checkbox"
                          checked={addTextShadow}
                          onChange={(e) => setAddTextShadow(e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-400"
                        />
                      </label>
                    </div>
                  </div>
                  <div className="px-3 pb-3">
                    <div className="flex gap-2">
                      {selectedAddTextId !== null && (
                        <button
                          onClick={handleDeleteAddText}
                          className="flex-shrink-0 py-2 px-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          削除
                        </button>
                      )}
                      <button
                        onClick={handleSaveAddText}
                        disabled={!addTextValue.trim()}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
                          addTextValue.trim()
                            ? 'text-white bg-purple-600 hover:bg-purple-700'
                            : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                        }`}
                      >
                        変更を追加
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Bottom toolbar */}
        {(textElements.length > 0 || !isDetecting) && (
          <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 flex w-[calc(100%-24px)] max-w-[1050px] flex-wrap items-center justify-center gap-2 bg-white rounded-xl shadow-2xl border border-gray-200 px-3 py-2">
            {/* Close */}
            <button
              onClick={handleCloseWithConfirm}
              disabled={isApplying}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              閉じる
            </button>

            <div className="w-px h-6 bg-gray-200" />

            {/* Mode toggle */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => switchMode('text')}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  editMode === 'text'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Type className="w-3.5 h-3.5" />
                テキスト編集
              </button>
              <button
                onClick={() => switchMode('region')}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  editMode === 'region'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <MousePointer2 className="w-3.5 h-3.5" />
                範囲選択
              </button>
              <button
                onClick={() => switchMode('addText')}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  editMode === 'addText'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Plus className="w-3.5 h-3.5" />
                テキスト追加
              </button>
              <button
                onClick={() => switchMode('naturalPhoto')}
                className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                  editMode === 'naturalPhoto'
                    ? 'bg-white text-emerald-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Camera className="w-3.5 h-3.5" />
                自然写真化
              </button>
            </div>

            {editMode === 'naturalPhoto' && (
              <button
                onClick={() => handleAddNaturalPhotoEdit()}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors flex items-center gap-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              >
                <Camera className="w-3.5 h-3.5" />
                全体に適用
              </button>
            )}

            <div className="w-px h-6 bg-gray-200" />

            {/* Change count */}
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <ClipboardList className="w-4 h-4" />
              <span className="font-medium" aria-live="polite">{changeCount === 0 ? '変更なし' : `未反映 ${changeCount}件`}</span>
            </div>

            {/* Undo */}
            <button
              onClick={handleUndo}
              disabled={undoStack.length === 0}
              className={`p-1.5 rounded-md transition-colors ${
                undoStack.length > 0 ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
              }`}
              title="元に戻す"
            >
              <Undo2 className="w-4 h-4" />
            </button>

            {/* Redo */}
            <button
              onClick={handleRedo}
              disabled={redoStack.length === 0}
              className={`p-1.5 rounded-md transition-colors ${
                redoStack.length > 0 ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
              }`}
              title="やり直す"
            >
              <Redo2 className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-gray-200" />

            {/* Discard */}
            <button
              onClick={handleDiscardWithConfirm}
              disabled={!hasChanges}
              className={`px-2.5 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                hasChanges ? 'text-gray-600 hover:bg-gray-100' : 'text-gray-300 cursor-not-allowed'
              }`}
            >
              破棄
            </button>

            {/* Apply */}
            <button
              onClick={handleApply}
              disabled={!hasChanges || isApplying}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                hasChanges && !isApplying
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isApplying ? (
                <span className="flex items-center gap-1.5">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {applyStatus}
                </span>
              ) : (
                '画像に反映して保存'
              )}
            </button>
          </div>
        )}

        {/* Error toast */}
        {error && (
          <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-red-500 text-white rounded-lg shadow-lg text-sm">
            {error}
          </div>
        )}
      </div>
    </div>
  );
};

export default SmartImageEditModal;
